import type { keyPair } from "@repo/models";

import { hasValue, styles } from "@repo/helpers";
import { useId } from "react";

import type { MarkupProps } from "./types";

import { InputComponent } from "../input";
import { Spinner } from "../spinner";
import { ToggleButton, ToggleButtonGroup } from "../toggleButton";
import style from "./style.module.scss";

export default function RadioButtonListMarkup(props: MarkupProps) {
	const { values, valueChanged, fullLength, clearButton, size, loaded = true, ...inputProps } = props;
	const { value: valueKvp, id, disabled, validationError } = inputProps;
	const value = hasValue(valueKvp) ? (valueKvp as keyPair).value : null;
	const large = size === "large";

	const uniqueId = `${id}${useId()}`;

	function renderRadioButtons() {
		if (!loaded)
			return (
				<Spinner tag="h5" right>
					{"Loading..."}
				</Spinner>
			);

		return (
			<ToggleButtonGroup
				className={styles(
					!large && !validationError && style.pillButton,
					(fullLength || large) && style.fullLength,
				)}
			>
				{values.map(renderToggleButton)}
			</ToggleButtonGroup>
		);
	}

	function renderToggleButton(option: keyPair) {
		const optionValue = option.id as any;
		const label = option.value;

		const key = `${uniqueId}${optionValue}`;
		return (
			<ToggleButton
				id={key}
				key={key}
				value={optionValue}
				variant={getVariant()}
				className={style.toggleButton}
				disabled={disabled}
				active={value === optionValue}
				onClick={valueChanged}
			>
				{label}
			</ToggleButton>
		);

		function getVariant() {
			if (disabled) return "outline-secondary";
			if (value === optionValue) {
				if (optionValue === false) return "outline-danger";
				if (optionValue === true) return "outline-success";
			}

			return "outline-primary";
		}
	}

	return (
		<InputComponent {...inputProps} addOnClassName={!large && !validationError && style.pillButton}>
			{renderRadioButtons()}
			{clearButton}
		</InputComponent>
	);
}
